import type { FC } from 'react';

import { Badge, Box, Expandable, Heading, Stack, Text } from '@eneco/sparky';
import { useMediaQuery } from '@eneco/sparky/hooks';
import type { BadgeProps, HeadingProps } from '@eneco/sparky/types';

import { ChatButton } from './ChatButton';
import { RevealPhoneNumber } from './RevealPhoneNumber';

/** A contact type as both lanes hand it over, read into one shape by `ContactMethods.tsx`. */
export interface ContactType {
  key: string;
  title?: string;
  text?: string;
  badgeText?: string;
  badgeColor?: string;
  openingHoursTitle?: string;
  openingHoursContent?: string;
  phoneNumber?: string;
  buttonText?: string;
  chatTopic?: string;
}

const ContactExpandableContent: FC<{ item: ContactType }> = ({ item }) => {
  const hasChatLink = !item.phoneNumber;

  return (
    <Expandable.Content>
      <Box paddingY="8" paddingLeft={{ initial: '2', md: '6' }}>
        <Stack gap="6">
          {item.openingHoursTitle && item.openingHoursContent && (
            <Stack gap="6">
              <Stack gap="2">
                <Text size="BodyM" weight="bold">
                  {item.openingHoursTitle}
                </Text>
                <Text as="p">{item.openingHoursContent}</Text>
              </Stack>
              {hasChatLink && (
                <Box>
                  <ChatButton buttonText={item.buttonText} chatTopic={item.chatTopic} />
                </Box>
              )}
              <RevealPhoneNumber label={item.buttonText} phoneNumber={item.phoneNumber} />
            </Stack>
          )}
        </Stack>
      </Box>
    </Expandable.Content>
  );
};

interface TriggerProps {
  title: string;
  headingLevel: HeadingProps['as'];
  badge: {
    color: BadgeProps['color'];
    text: string;
  };
  subtitle?: string;
}

const ContactExpandableTrigger: FC<TriggerProps> = ({ title, headingLevel, badge, subtitle }) => {
  const isMediumBreakpoint = useMediaQuery('md');
  return (
    <Expandable.Trigger label={title}>
      <Stack gap="2">
        <Stack direction="row" gap={{ initial: '2', md: '4' }} alignY="center">
          <Heading as={headingLevel} size={{ initial: '3XS', md: 'XS' }}>
            {title}
          </Heading>
          <Stack.Item>
            <Badge size={isMediumBreakpoint ? 'M' : 'S'} color={badge.color}>
              {badge.text}
            </Badge>
          </Stack.Item>
        </Stack>
        {subtitle && <Text as="p">{subtitle}</Text>}
      </Stack>
    </Expandable.Trigger>
  );
};

export const ContactExpandable: FC<{ item: ContactType }> = ({ item }) => {
  return (
    <Expandable>
      <Stack>
        <ContactExpandableTrigger
          title={item.title ?? ''}
          badge={{
            color: item.badgeColor as BadgeProps['color'],
            text: item.badgeText ?? '',
          }}
          subtitle={item.text}
          headingLevel="h3"
        />
        <ContactExpandableContent item={item} />
      </Stack>
    </Expandable>
  );
};
